import React, {useEffect, useMemo} from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import Analytics from '@popularpays/analytics';
import analyticsEvents from '@/lib/analyticsEvents';
import {
  ERROR_MESSAGE_PARAM_KEY,
  ERROR_PROVIDER_PARAM_KEY,
  TOAST_PARAM_KEY,
} from '@/config/constants';

function parseBoolean(value: string | null): boolean {
  if (!value) {
    return false;
  }
  return value.toLowerCase() === 'true' || value === '1';
}

function stripCallbackFromPathname(pathname: string): string {
  const stripped = pathname.replace(/\/callback\/?$/, '');
  return stripped.length === 0 ? '/' : stripped;
}

export default function CallbackRoute() {
  const location = useLocation();

  const {nextPath, cleanedSearchParams, provider, success, redirectPathParam} =
    useMemo(() => {
      const searchParams = new URLSearchParams(location.search);

      const providerParam = searchParams.get('provider');
      const successParam = parseBoolean(searchParams.get('success'));
      const redirectPath = searchParams.get('redirectPath');

      // Build destination path by stripping trailing /callback
      const targetPath = stripCallbackFromPathname(location.pathname);

      // Keep only whitelisted params when redirecting
      const ALLOWED_PARAM_KEYS = new Set<string>([
        ERROR_MESSAGE_PARAM_KEY,
        ERROR_PROVIDER_PARAM_KEY,
        TOAST_PARAM_KEY,
      ]);
      const keptParams = new URLSearchParams();
      for (const [key, value] of searchParams.entries()) {
        if (ALLOWED_PARAM_KEYS.has(key)) {
          keptParams.set(key, value);
        }
      }

      return {
        nextPath: targetPath,
        cleanedSearchParams: keptParams,
        provider: providerParam,
        success: successParam,
        redirectPathParam: redirectPath,
      };
    }, [location.pathname, location.search]);

  useEffect(() => {
    const payload: Record<string, unknown> = {
      event: analyticsEvents.LINK_SOCIAL_PROFILE.CHECK_LINK_SOCIAL_PROFILE,
      method: provider || undefined,
      error_message: success ? 'success' : 'failed',
    };

    if (redirectPathParam) {
      payload.link_url = redirectPathParam;
    }

    // Delay to mimic Ember timing and ensure pageview settles
    const timer = setTimeout(() => {
      Analytics.dispatchEvent(
        analyticsEvents.LINK_SOCIAL_PROFILE.CHECK_LINK_SOCIAL_PROFILE,
        payload
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [success, provider, redirectPathParam]);

  const searchString = cleanedSearchParams.toString();
  const destination = `${nextPath}${searchString ? `?${searchString}` : ''}`;

  return <Navigate to={destination} replace />;
}
